<div class="cipx-form-container file-cabinet-dropzone w-full bg-gray-100 dark:bg-transparent flex flex-col h-full relative"
     (dragenter)="onDragEnter($event)"
     (dragover)="onDragOver($event)"
     (dragleave)="onDragLeave($event)"
     (drop)="onDrop($event)">
  <input #fileInput type="file" [multiple]="true" (change)="onFileSelected($event)" class="hidden" />

  <!-- Header with Breadcrumbs and View Switcher (above drag overlay so Upload stays clickable) -->
  <div class="file-cabinet-header relative z-20 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 sm:p-6 sm:py-6 md:px-8 border-b bg-card dark:bg-transparent flex-shrink-0">
    <div class="min-w-0 flex-auto w-full sm:w-auto">
      <div class="flex items-center font-medium text-secondary min-w-0">
        <!-- Breadcrumbs -->
        <ng-container *ngIf="path.length; else homeLabel">
          <div class="file-cabinet-breadcrumbs flex items-center gap-1 sm:gap-2 text-sm sm:text-base min-w-0 overflow-x-auto">
            <a class="text-primary cursor-pointer flex-shrink-0" (click)="navigateBack(-1)">Home</a>
            <ng-container *ngFor="let p of path; let i = index; let last = last; trackBy: trackByFn">
              <div class="flex-shrink-0 text-hint">/</div>
              <ng-container *ngIf="!last">
                <a class="text-primary cursor-pointer flex-shrink-0 max-w-[8rem] sm:max-w-none truncate" (click)="navigateBack(i)" [matTooltip]="p.name">{{ p.name }}</a>
              </ng-container>
              <ng-container *ngIf="last">
                <div class="flex-shrink-0 font-semibold text-default max-w-[10rem] sm:max-w-none truncate" [matTooltip]="p.name">{{ p.name }}</div>
              </ng-container>
            </ng-container>
          </div>
        </ng-container>
        <ng-template #homeLabel>
          <div class="text-sm sm:text-base font-semibold text-default">Home</div>
        </ng-template>
      </div>
    </div>

    <!-- View Switcher & Actions -->
    <div class="file-cabinet-toolbar flex items-center justify-between sm:justify-end gap-1 sm:gap-2 w-full sm:w-auto flex-shrink-0">
      <button
        type="button"
        *ngIf="canManageFiles"
        mat-flat-button
        [color]="'primary'"
        (click)="$event.preventDefault(); $event.stopPropagation(); openUploadDialog()"
        class="file-cabinet-upload-btn">
        <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
        <span class="ml-2 hidden xs:inline sm:inline">Upload</span>
      </button>

      <div class="flex items-center gap-1 sm:gap-2 ml-auto sm:ml-0">
        <ng-container *ngIf="isInFolder">
          <button
            mat-icon-button
            [matMenuTriggerFor]="fileSortMenu"
            [matTooltip]="fileSortOrder === 'newest' ? 'Newest to oldest' : 'Oldest to newest'">
            <mat-icon [svgIcon]="'heroicons_outline:switch-vertical'"></mat-icon>
          </button>
          <mat-menu #fileSortMenu="matMenu">
            <button mat-menu-item (click)="setFileSortOrder('newest')">
              <mat-icon *ngIf="fileSortOrder === 'newest'">check</mat-icon>
              <span [class.ml-8]="fileSortOrder !== 'newest'">Newest to oldest</span>
            </button>
            <button mat-menu-item (click)="setFileSortOrder('oldest')">
              <mat-icon *ngIf="fileSortOrder === 'oldest'">check</mat-icon>
              <span [class.ml-8]="fileSortOrder !== 'oldest'">Oldest to newest</span>
            </button>
          </mat-menu>
        </ng-container>
        <cipx-pill-switcher
          [options]="viewOptions"
          [value]="viewType"
          (valueChange)="setViewType($any($event))">
        </cipx-pill-switcher>
      </div>
    </div>
  </div>

  <!-- Items list -->
  <cipx-scrollable-content
    class="file-cabinet-content flex-auto relative"
    [noPadding]="true"
    *ngIf="currentFolder"
    (click)="onMainAreaClick($event, fileInput)">
    <!-- Drag overlay only over content (not header) so Upload/sort controls stay clickable -->
    <div
      class="file-cabinet-drag-overlay absolute inset-0 z-[100] bg-primary bg-opacity-10 border-2 border-primary border-dashed flex items-center justify-center pointer-events-none"
      [class.file-cabinet-drag-overlay--active]="isDragging"
      aria-hidden="true">
      <div class="bg-card p-4 sm:p-8 rounded-2xl shadow-xl flex flex-col items-center mx-4 text-center">
        <mat-icon class="icon-size-12 text-primary mb-2 sm:mb-4" [svgIcon]="'heroicons_outline:upload'"></mat-icon>
        <div class="text-lg sm:text-2xl font-bold text-primary">Drop files to upload</div>
      </div>
    </div>
    <!-- Centered drag-drop affordance when folder has content (does not block clicks) -->
    <div
      class="file-cabinet-drop-watermark"
      *ngIf="canManageFiles && !isDragging && (viewType === 'tile' ? (getFolders(currentFolder.data).length > 0 || getFiles(currentFolder.data).length > 0) : !!dataSource.data?.length)"
      aria-hidden="true">
      <div class="file-cabinet-drop-hint file-cabinet-drop-hint--watermark">
        <mat-icon class="file-cabinet-drop-hint-icon" [svgIcon]="'heroicons_outline:cloud-upload'"></mat-icon>
        <div class="file-cabinet-drop-hint-title">Drag &amp; drop files here</div>
      </div>
    </div>

    <ng-container *ngIf="viewType === 'tile'">
      <ng-container
        *ngIf="getFolders(currentFolder.data).length > 0 || getFiles(currentFolder.data).length > 0; else noItems">
        <div class="file-cabinet-items p-3 sm:p-6 md:p-8 space-y-6 sm:space-y-8 relative z-10">
          <!-- Folders -->
          <ng-container *ngIf="getFolders(currentFolder.data).length > 0">
            <div>
              <div class="file-cabinet-folder-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3 mt-2">
                <ng-container *ngFor="let folder of getFolders(currentFolder.data); trackBy: trackByFn">
                  <a
                    class="file-folder-card group bg-card"
                    (click)="navigateToFolder(folder)"
                    [matTooltip]="getFolderDisplayName(folder)"
                    [attr.aria-label]="getFolderDisplayName(folder)">
                    <div class="file-folder-icon-wrap">
                      <mat-icon
                        class="file-folder-icon text-hint"
                        [svgIcon]="'heroicons_solid:folder'"></mat-icon>
                    </div>
                    <div class="file-folder-content min-w-0">
                      <div class="file-folder-name">{{ getFolderDisplayName(folder) }}</div>
                      <div class="file-folder-count text-secondary">{{ formatItemCount(getFolderItemCount(folder)) }}</div>
                    </div>
                  </a>
                </ng-container>
              </div>
            </div>
          </ng-container>

          <!-- Files -->
          <ng-container *ngIf="getFiles(currentFolder.data).length > 0">
            <div>
              <div class="font-medium flex items-center justify-between text-sm sm:text-base px-1">
                <span></span>
                <span class="text-xs font-normal text-secondary" *ngIf="isInFolder">
                  {{ fileSortOrder === 'newest' ? 'Newest to oldest' : 'Oldest to newest' }}
                </span>
              </div>
              <div class="file-cabinet-file-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7 gap-2 sm:gap-3 mt-2">
                <ng-container *ngFor="let file of getFiles(currentFolder.data); trackBy: trackByFn">
                  <div
                    class="file-card relative w-full h-full shadow rounded-xl sm:rounded-2xl bg-card group overflow-visible"
                    [class.opacity-70]="isDeleted(file)">
                    <div class="file-card-actions absolute z-20 top-1.5 right-1.5 flex flex-row sm:flex-col space-x-0.5 sm:space-x-0 sm:space-y-1">
                      <button
                        [color]="'primary'"
                        class="w-8 h-8 min-h-8"
                        (click)="$event.preventDefault(); $event.stopPropagation(); downloadFile(file)"
                        mat-icon-button
                        matTooltip="Download">
                        <mat-icon>download</mat-icon>
                      </button>
                      <button
                        *ngIf="canManageFiles && !isDeleted(file)"
                        [color]="'warn'"
                        class="w-8 h-8 min-h-8"
                        (click)="$event.preventDefault(); $event.stopPropagation(); deleteFile(file)"
                        mat-icon-button
                        matTooltip="Delete">
                        <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                      </button>
                      <button
                        *ngIf="canManageFiles && isDeleted(file)"
                        [color]="'primary'"
                        class="w-8 h-8 min-h-8"
                        (click)="$event.preventDefault(); $event.stopPropagation(); restoreFile(file)"
                        mat-icon-button
                        matTooltip="Undo deletion">
                        <mat-icon>restore_from_trash</mat-icon>
                      </button>
                    </div>
                    <a class="file-card-body relative z-10 flex flex-row sm:flex-col items-start sm:items-stretch gap-3 sm:gap-0 p-3 sm:p-4 pr-24 sm:pr-10 cursor-pointer h-full" (click)="viewFile(file)">
                      <div class="file-card-icon flex items-center justify-center flex-shrink-0 pt-0 sm:pt-1 pb-0 sm:pb-2">
                        <div class="relative">
                          <mat-icon
                            class="icon-size-10 text-hint opacity-50"
                            [svgIcon]="'heroicons_solid:document'"></mat-icon>
                          <div
                            class="absolute left-0 bottom-0 px-1.5 rounded text-[10px] sm:text-xs font-semibold leading-4 sm:leading-5 text-white"
                            [ngClass]="getExtensionColorClass(file.file_extension)">
                            {{ file.file_extension?.toUpperCase() || 'FILE' }}
                          </div>
                        </div>
                      </div>
                      <div class="file-card-content flex flex-col flex-auto text-left text-sm font-medium min-w-0 w-full h-full">
                        <div
                          class="file-card-name break-words whitespace-normal leading-snug"
                          [class.line-through]="isDeleted(file)"
                          [class.text-secondary]="isDeleted(file)">
                          {{ getFileDisplayName(file) }}
                        </div>
                        <div class="file-card-meta pt-1 space-y-0.5">
                          <div class="text-xs text-secondary break-words whitespace-normal" *ngIf="file.file_purpose_label || file.related_file_type_label">
                            {{ file.file_purpose_label || file.related_file_type_label }}
                          </div>
                          <div class="text-xs text-secondary">
                            {{ formatFileSize(file.file_size) }}
                            <ng-container *ngIf="file.ctime"> · {{ file.ctime | date: 'mediumDate' }}</ng-container>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </div>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="viewType === 'tree'">
      <div class="file-cabinet-tree p-2 sm:p-4" *ngIf="dataSource.data?.length; else noItems">
        <mat-tree [dataSource]="dataSource" [treeControl]="treeControl" class="file-tree">
          <!-- Leaf nodes (files or empty folders) -->
          <mat-tree-node *matTreeNodeDef="let node" class="file-tree-node">
            <!-- File leaf -->
            <div
              class="file-tree-row file-tree-file group"
              *ngIf="!isFolder(node)"
              [class.file-tree-row-deleted]="isDeleted(node)"
              (click)="viewFile(node)">
              <span class="file-tree-indent" aria-hidden="true"></span>
              <mat-icon class="file-tree-icon text-hint flex-shrink-0" [svgIcon]="'heroicons_solid:document'"></mat-icon>
              <div class="file-tree-meta min-w-0 flex-auto">
                <div class="file-tree-title-row min-w-0">
                  <span
                    class="file-tree-title"
                    [class.line-through]="isDeleted(node)"
                    [class.text-secondary]="isDeleted(node)">
                    {{ getFileDisplayName(node) }}
                  </span>
                  <span
                    class="file-tree-ext"
                    *ngIf="node.file_extension"
                    [ngClass]="getExtensionColorClass(node.file_extension)">
                    {{ node.file_extension | uppercase }}
                  </span>
                </div>
                <div class="file-tree-subtitle">
                  <ng-container *ngIf="node.file_purpose_category_label || node.file_purpose_label || node.related_file_type_label">
                    {{ node.file_purpose_category_label ||  node.file_purpose_label || node.related_file_type_label }}
                    <span class="mx-1">·</span>
                  </ng-container>
                  {{ formatFileSize(node.file_size) }}
                  <ng-container *ngIf="node.ctime"> · {{ node.ctime | date: 'mediumDate' }}</ng-container>
                  <ng-container *ngIf="isDeleted(node)"> · <span class="text-warn font-medium">Deleted</span></ng-container>
                </div>
              </div>
              <div class="file-tree-actions flex-shrink-0" (click)="$event.stopPropagation()">
                <button
                  mat-icon-button
                  [color]="'primary'"
                  (click)="downloadFile(node)"
                  matTooltip="Download">
                  <mat-icon>download</mat-icon>
                </button>
                <button
                  *ngIf="canManageFiles && !isDeleted(node)"
                  mat-icon-button
                  [color]="'warn'"
                  (click)="deleteFile(node)"
                  matTooltip="Delete">
                  <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                </button>
                <button
                  *ngIf="canManageFiles && isDeleted(node)"
                  mat-icon-button
                  [color]="'primary'"
                  (click)="restoreFile(node)"
                  matTooltip="Undo deletion">
                  <mat-icon>restore_from_trash</mat-icon>
                </button>
              </div>
            </div>

            <!-- Empty folder leaf -->
            <div
              class="file-tree-row file-tree-folder group"
              *ngIf="isFolder(node)"
              [class.file-tree-row-selected]="selectedFolder === node"
              (click)="selectFolder(node)">
              <span class="file-tree-indent" aria-hidden="true"></span>
              <mat-icon class="file-tree-icon text-hint flex-shrink-0" [svgIcon]="'heroicons_solid:folder'"></mat-icon>
              <span class="file-tree-title font-medium min-w-0 flex-auto truncate">{{ getFolderDisplayName(node) }}</span>
              <span class="file-tree-subtitle flex-shrink-0">{{ formatItemCount(0) }}</span>
            </div>
          </mat-tree-node>

          <!-- Expandable folder nodes -->
          <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild">
            <div class="file-tree-branch">
              <div
                class="file-tree-row file-tree-folder group"
                [class.file-tree-row-selected]="selectedFolder === node"
                [class.file-tree-row-expanded]="treeControl.isExpanded(node)">
                <button
                  mat-icon-button
                  class="file-tree-toggle flex-shrink-0"
                  matTreeNodeToggle
                  [attr.aria-label]="'Toggle ' + getFolderDisplayName(node)"
                  (click)="$event.stopPropagation()">
                  <mat-icon class="mat-icon-rtl-mirror">
                    {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                  </mat-icon>
                </button>
                <div
                  class="file-tree-folder-main min-w-0 flex-auto flex items-center gap-2 cursor-pointer"
                  (click)="selectFolder(node, true)">
                  <mat-icon
                    class="file-tree-icon text-hint flex-shrink-0"
                    [svgIcon]="treeControl.isExpanded(node) ? 'heroicons_solid:folder-open' : 'heroicons_solid:folder'">
                  </mat-icon>
                  <span class="file-tree-title font-medium min-w-0 flex-auto truncate">{{ getFolderDisplayName(node) }}</span>
                  <span class="file-tree-subtitle flex-shrink-0">{{ formatItemCount(getFolderItemCount(node)) }}</span>
                </div>
              </div>
              <div
                class="file-tree-children"
                [class.file-tree-children-hidden]="!treeControl.isExpanded(node)"
                role="group">
                <ng-container matTreeNodeOutlet></ng-container>
              </div>
            </div>
          </mat-nested-tree-node>
        </mat-tree>
      </div>
    </ng-container>

    <!-- No items template -->
    <ng-template #noItems>
      <div class="file-cabinet-empty flex flex-auto flex-col items-center justify-center bg-gray-100 dark:bg-transparent p-6 text-center">
        <ng-container *ngIf="canManageFiles; else emptyReadOnly">
          <div
            class="file-cabinet-drop-hint"
            (click)="triggerBrowse(fileInput); $event.stopPropagation()"
            role="button"
            tabindex="0"
            (keydown.enter)="triggerBrowse(fileInput)"
            (keydown.space)="$event.preventDefault(); triggerBrowse(fileInput)">
            <mat-icon class="file-cabinet-drop-hint-icon" [svgIcon]="'heroicons_outline:cloud-upload'"></mat-icon>
            <div class="file-cabinet-drop-hint-title">Drag &amp; drop files here</div>
            <div class="file-cabinet-drop-hint-subtitle">or click to browse</div>
          </div>
        </ng-container>
        <ng-template #emptyReadOnly>
          <mat-icon class="icon-size-16" [svgIcon]="'heroicons_outline:folder-open'"></mat-icon>
          <div class="mt-3 sm:mt-4 text-lg sm:text-2xl font-semibold tracking-tight text-secondary">There are no items!</div>
        </ng-template>
      </div>
    </ng-template>
  </cipx-scrollable-content>
</div>
